import { option } from 'fp-ts';
import { useEffect } from 'react';
import styled from 'styled-components';
import { SelectContainer, SelectWrapper as RawSelectWrapper } from 'ui/selects';
import { HelpToggletip } from 'ui/tooltip';
import { resetQueryBuilder } from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { CatalogSelector, useCatalogContext } from 'modules/catalog/catalogSelector';
import { camelCase } from 'utils/stringHelpers';
import { isDefined } from 'utils/tsHelpers';
import { ClientDataType, CLIENT_DATA_TYPES } from 'utils/dataTypes';
import { FieldSelector } from './FieldSelector';
import { ResetButton } from './ResetButton';
import { CatalogFieldWarningBanner } from './CatalogFieldWarningBanner';
import { useCatalogSelectionContext } from './contexts/CatalogSelectionContext';
import { useTranslation } from '../../hooks';
import { useCatalogPreviewContext } from './contexts/CatalogPreviewContext';
import { useCatalogQueryContext } from '../../contexts/catalogQuery';
import { useCatInSegQueryBuilderV2FeatureFlag } from './hooks/useCatInSegQueryBuilderV2FeatureFlag';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing.values.double};
`;

const CatalogSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing.values.half};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  font-weight: bold;
  column-gap: ${({ theme }) => theme.spacing.values.half};
`;

const StyledText = styled.span`
  color: ${({ theme }) => theme.colors.primaryColors.cyan100};
`;

const SelectorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Selection = styled.div`
  ${({ theme }) => theme.styles.body.body2Strong};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.values.half};
`;

const SelectWrapper = styled.div<{ width: number }>`
  ${SelectContainer} {
    width: ${({ theme, width }) => theme.spacing.utils.pxToRem(width)};
  }
  ${RawSelectWrapper} {
    min-width: ${({ theme, width }) => theme.spacing.utils.pxToRem(width)};
  }
`;

export const DATA_TEST = {
  selector: 'catalog-selector',
};

const catalogSelectorWidth = 300;
const catalogFieldSelectorWidth = 350;

const getDataTypeMessage = (dataType?: ClientDataType) => {
  if (dataType === camelCase(CLIENT_DATA_TYPES.USER)) return 'user profile';
  else if (dataType === camelCase(CLIENT_DATA_TYPES.CUSTOM_EVENT)) return 'custom event';
  else return 'event';
};

export const Selector = () => {
  const { t } = useTranslation();
  const { closePreview } = useCatalogPreviewContext();
  const isFeatureFlagged = useCatInSegQueryBuilderV2FeatureFlag();
  const { catalogQueryDetails, resetCatalogQuery } = useCatalogQueryContext();
  const { update } = useCatalogSelectionContext();
  const { catalog, changeFieldDetails, catalogFieldDetails } = useCatalogContext();
  const searchField = catalogQueryDetails?.searchField;
  const dataType = catalogQueryDetails?.dataType;
  const catalogId = option.toUndefined(catalog)?.id;

  const catalogName = option.toUndefined(catalog)?.name;
  const catalogNameFromDetails = catalogQueryDetails?.catalogQuery?.catalogName;
  /** only use this useEffect if feature flag is off */
  useEffect(() => {
    if (
      !isFeatureFlagged &&
      catalogNameFromDetails &&
      catalogName &&
      catalogNameFromDetails !== catalogName
    ) {
      update(false); // change isMatchingONKey to false
      changeFieldDetails(option.none); // reset the catalog field
      resetQueryBuilder(); // reset the builder
      closePreview(); // close preview table if open
    }
  }, [catalogName, catalogNameFromDetails]);

  /** only use this useEffect if feature flag is on */
  /** update catalog field state */
  useEffect(() => {
    const catalogName = option.toUndefined(catalog)?.name;
    const catalogQueryName = catalogQueryDetails?.catalogQuery?.catalogName;
    if (isFeatureFlagged && isDefined(catalogId) && catalogName !== catalogQueryName) {
      update(false); // change isMatchingONKey to false
      changeFieldDetails(option.none); // reset the catalog field
      closePreview(); // close preview table if open
    }
  }, [catalogId]);

  /** only use this useEffect if feature flag is on */
  /** reset catalogQuery state */
  useEffect(() => {
    if (isFeatureFlagged && option.isNone(catalogFieldDetails)) {
      resetCatalogQuery();
    }
  }, [catalogFieldDetails]);

  return (
    <Wrapper>
      <CatalogFieldWarningBanner />
      <CatalogSelectWrapper data-test={DATA_TEST.selector}>
        <TextWrapper>
          <span>{t('catalogQuery.selector', { dataType: getDataTypeMessage(dataType) })}</span>
          <StyledText>{searchField}</StyledText>
        </TextWrapper>
        <SelectorWrapper>
          <Selection>
            <SelectWrapper width={catalogSelectorWidth}>
              <CatalogSelector />
            </SelectWrapper>
            <SelectWrapper width={catalogFieldSelectorWidth}>
              <FieldSelector />
            </SelectWrapper>
            <HelpToggletip
              iconProps={{ height: 24, width: 24 }}
              tooltipContent={t('catalogQuery.fieldSelectHelpTooltip', {
                dataType: getDataTypeMessage(dataType),
              })}
            />
          </Selection>
          <ResetButton />
        </SelectorWrapper>
      </CatalogSelectWrapper>
    </Wrapper>
  );
};
